import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { convertDateFormat } from "../../utils/Common";

const DebouncedInput = ({
  value: initValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initValue);
  useEffect(() => {
    setValue(initValue);
  }, [initValue]);

  // *  0.5s after set value in state
  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

const ReportTable = (props) => {
  const { data } = props;
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("", {
      id: "S.No",
      cell: (info) => <span>{info.row.index + 1}</span>,
      header: "S.No",
      width: 10,
    }),
    columnHelper.accessor("task_name", {
      cell: (info) => <span>{info.getValue()}</span>,
      header: "Task Name",
      width: 30,
    }),
    columnHelper.accessor("meeting_name", {
      cell: (info) => <span>{info.getValue()}</span>,
      header: "Meeting Name",
      width: 30,
    }),
    columnHelper.accessor("user_name", {
      cell: (info) => <span>{info.getValue()}</span>,
      header: "Task Owner",
      width: 10,
    }),
    columnHelper.accessor("createdAt", {
      cell: (info) => {
        let createdAt = convertDateFormat(info.getValue(), "DD-MM-YYYY") || '-';
        return <span>{createdAt}</span>;
      },
      header: "Task Created At",
      width: 10,
    }),
    columnHelper.accessor("task_done", {
      cell: (info) => {
        const task_status = (info.getValue() == true) ? "Complete": "Incomplete";
        return <span>{task_status}</span>;
      },
      header: "Status",
      width: 10,
    }),
  ];

  const [globalFilter, setGlobalFilter] = useState("");
  // console.log("tableData::::", data)
  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
    },
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const buttonCss = "disabled:opacity-30 s23-extra-step lg:text-[18x] py-[10px] flex items-center justify-center gap-[5px] md:text-[16px] text-[12px] bg-[#07457F] text-[white] lg:px-[10px] px-[5px] cursor-pointer";
  return (
    <>
      <div className="flex justify-between mb-2">
        <div className="w-full flex items-center gap-1">
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            className="p-2 bg-transparent outline-none border-b-2 w-1/5 focus:w-1/3 duration-300"
            placeholder="Search all columns..."
          />
        </div>
      </div>
      <table className="border border-gray-700 w-full text-left">
        <thead className="bg-[#07457F] text-white">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="capitalize px-3.5 py-2">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row, i) => (
              <tr
                key={row.id}
                className={` 
                ${i % 2 === 0 ? "" : "bg-gray-200"}
                `}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3.5 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr className="text-center h-32">
              <td colSpan={12}>No Record Found!</td>
            </tr>
          )}
        </tbody>
      </table>
      {/* pagination */}
      <div className="flex items-center justify-end mt-2 gap-2">
        <button
          onClick={() => {
            table.previousPage();
          }}
          disabled={!table.getCanPreviousPage()}
          // className="p-1 border border-gray-300 px-2 disabled:opacity-30 "
          className={`${buttonCss} rounded-l-[10px]`}
        >
          {"<"}
        </button>
        <button
          onClick={() => {
            table.nextPage();
          }}
          disabled={!table.getCanNextPage()}
          className={`${buttonCss} rounded-r-[10px]`}
        >
          {">"}
        </button>

        <span className="flex items-center gap-1">
          <div>Page</div>
          <strong>
            {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </strong>
        </span>
        <span className="flex items-center gap-1">
          | Go to page:
          <input
            type="number"
            defaultValue={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
            className="border p-1 rounded w-16 bg-transparent"
          />
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
          className="p-2 bg-transparent"
        >
          {[10, 20, 30, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>
    </>
  );
};

export default ReportTable;